import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, doc, updateDoc, deleteDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useLang, LangToggle } from '../LanguageContext';
import { t } from '../i18n';

const WILAYAS = [
  'Adrar','Chlef','Laghouat','Oum El Bouaghi','Batna','Béjaïa','Biskra','Béchar',
  'Blida','Bouira','Tamanrasset','Tébessa','Tlemcen','Tiaret','Tizi Ouzou','Alger',
  'Djelfa','Jijel','Sétif','Saïda','Skikda','Sidi Bel Abbès','Annaba','Guelma',
  'Constantine','Médéa','Mostaganem','M\'Sila','Mascara','Ouargla','Oran','El Bayadh',
  'Illizi','Bordj Bou Arréridj','Boumerdès','El Tarf','Tindouf','Tissemsilt','El Oued',
  'Khenchela','Souk Ahras','Tipaza','Mila','Aïn Defla','Naâma','Aïn Témouchent',
  'Ghardaïa','Relizane','Timimoun','Bordj Badji Mokhtar','Ouled Djellal','Béni Abbès',
  'In Salah','In Guezzam','Touggourt','Djanet','El M\'Ghair','El Meniaa'
];

const FORM_VIDE = {
  prenom: '', nom: '', email: '', motDePasse: '',
  objectifVisites: 60, objectifClients: 3, variableTotal: 0, wilayas: [],
};

export default function GestionCommerciaux({ onClose }) {
  const [commerciaux, setCommerciaux] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vue, setVue] = useState('liste'); // 'liste' | 'ajouter' | 'modifier'
  const [form, setForm] = useState(FORM_VIDE);
  const [commEdite, setCommEdite] = useState(null);
  const { lang, isRTL } = useLang();
  const [saving, setSaving] = useState(false);
  const [erreur, setErreur] = useState('');
  const [succes, setSucces] = useState('');
  const [confirmSuppr, setConfirmSuppr] = useState(null);

  useEffect(() => { charger(); }, []);

  const charger = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(query(collection(db, 'users'), where('role', '==', 'commercial')));
      setCommerciaux(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { console.log(e); }
    setLoading(false);
  };

  // ── AJOUTER ──
  const ouvrirAjouter = () => {
    setForm(FORM_VIDE);
    setErreur('');
    setSucces('');
    setVue('ajouter');
  };

  const creerCommercial = async () => {
    setErreur('');
    if (!form.prenom.trim() || !form.nom.trim() || !form.email.trim() || !form.motDePasse.trim()) {
      setErreur('Prénom, Nom, Email et Mot de passe sont obligatoires.');
      return;
    }
    if (form.motDePasse.length < 6) {
      setErreur('Le mot de passe doit contenir au moins 6 caractères.');
      return;
    }
    setSaving(true);
    try {
      // Créer le compte Firebase Auth via API REST (sans déconnecter le manager)
      const apiKey = 'AIzaSyC5rHBWCeR8eldXBdiyJEmIsl3TtCTXBHc';
      const res = await fetch(`https://identitytoolkit.googleapis.com/v1/accounts:signUp?key=${apiKey}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: form.email, password: form.motDePasse, returnSecureToken: true }),
      });
      const data = await res.json();
      if (data.error) {
        if (data.error.message === 'EMAIL_EXISTS') setErreur('Cet email est déjà utilisé.');
        else setErreur('Erreur : ' + data.error.message);
        setSaving(false);
        return;
      }
      const uid = data.localId;
      // Créer le document Firestore
      await setDoc(doc(db, 'users', uid), {
        prenom: form.prenom.trim(),
        nom: form.nom.trim(),
        email: form.email.trim().toLowerCase(),
        role: 'commercial',
        objectifVisites: parseInt(form.objectifVisites) || 60,
        objectifClients: parseInt(form.objectifClients) || 3,
        variableTotal: parseInt(form.variableTotal) || 0,
        wilayas: form.wilayas,
        createdAt: new Date().toISOString(),
      });
      setSucces(`✅ ${form.prenom} ${form.nom} ajouté avec succès !`);
      setForm(FORM_VIDE);
      await charger();
      setTimeout(() => { setSucces(''); setVue('liste'); }, 2000);
    } catch (e) {
      setErreur('Erreur lors de la création : ' + e.message);
    }
    setSaving(false);
  };

  // ── MODIFIER ──
  const ouvrirModifier = (c) => {
    setCommEdite(c);
    setForm({
      prenom: c.prenom || '',
      nom: c.nom || '',
      email: c.email || '',
      motDePasse: '',
      objectifVisites: c.objectifVisites || 60,
      objectifClients: c.objectifClients || 3,
      variableTotal: c.variableTotal || 0,
      wilayas: c.wilayas || [],
    });
    setErreur('');
    setSucces('');
    setVue('modifier');
  };

  const sauvegarderModif = async () => {
    setErreur('');
    if (!form.prenom.trim() || !form.nom.trim()) {
      setErreur('Prénom et Nom sont obligatoires.');
      return;
    }
    setSaving(true);
    try {
      await updateDoc(doc(db, 'users', commEdite.id), {
        prenom: form.prenom.trim(),
        nom: form.nom.trim(),
        objectifVisites: parseInt(form.objectifVisites) || 60,
        objectifClients: parseInt(form.objectifClients) || 3,
        variableTotal: parseInt(form.variableTotal) || 0,
        wilayas: form.wilayas,
      });
      setSucces(`✅ ${form.prenom} ${form.nom} mis à jour !`);
      await charger();
      setTimeout(() => { setSucces(''); setVue('liste'); }, 2000);
    } catch (e) {
      setErreur('Erreur : ' + e.message);
    }
    setSaving(false);
  };

  // ── SUPPRIMER ──
  const supprimerCommercial = async (c) => {
    setSaving(true);
    try {
      await deleteDoc(doc(db, 'users', c.id));
      setConfirmSuppr(null);
      await charger();
    } catch (e) {
      alert('Erreur suppression : ' + e.message);
    }
    setSaving(false);
  };

  const toggleWilaya = (w) => {
    setForm(f => ({
      ...f,
      wilayas: f.wilayas.includes(w) ? f.wilayas.filter(x => x !== w) : [...f.wilayas, w]
    }));
  };

  // ── FORMULAIRE (partagé ajouter/modifier) ──
  const renderForm = (titre, onSubmit, submitLabel) => (
    <div style={styles.app}>
      <div style={styles.topbar}>
        <button onClick={() => setVue('liste')} style={styles.backBtn}>←</button>
        <div style={styles.topbarTitle}>{titre}</div>
      </div>
      <div style={styles.content}>
        {erreur && <div style={styles.erreur}>{erreur}</div>}
        {succes && <div style={styles.succes}>{succes}</div>}

        <div style={styles.card}>
          <div style={styles.sectionLabel}>Identité</div>
          {[
            { label: 'Prénom *', key: 'prenom', type: 'text' },
            { label: 'Nom *', key: 'nom', type: 'text' },
          ].map(f => (
            <div key={f.key} style={{ marginBottom: 12 }}>
              <div style={styles.fieldLabel}>{f.label}</div>
              <input style={styles.input} value={form[f.key]} type={f.type}
                placeholder={f.label.replace(' *', '')}
                onChange={e => setForm(x => ({ ...x, [f.key]: e.target.value }))} />
            </div>
          ))}
        </div>

        <div style={styles.card}>
          <div style={styles.sectionLabel}>Connexion</div>
          <div style={{ marginBottom: 12 }}>
            <div style={styles.fieldLabel}>Email *</div>
            <input style={{ ...styles.input, ...(vue === 'modifier' ? { background: '#f5f5f5', color: '#aaa' } : {}) }}
              value={form.email} type="email" placeholder="email@exemple.com"
              readOnly={vue === 'modifier'}
              onChange={e => setForm(x => ({ ...x, email: e.target.value }))} />
            {vue === 'modifier' && <div style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>L'email ne peut pas être modifié ici.</div>}
          </div>
          {vue === 'ajouter' && (
            <div style={{ marginBottom: 12 }}>
              <div style={styles.fieldLabel}>Mot de passe * (min. 6 caractères)</div>
              <input style={styles.input} value={form.motDePasse} type="password"
                placeholder="••••••••"
                onChange={e => setForm(x => ({ ...x, motDePasse: e.target.value }))} />
            </div>
          )}
        </div>

        <div style={styles.card}>
          <div style={styles.sectionLabel}>Objectifs & Variable</div>
          {[
            { label: 'Objectif visites / mois', key: 'objectifVisites' },
            { label: 'Objectif nouveaux clients / mois', key: 'objectifClients' },
            { label: 'Variable total DA / mois', key: 'variableTotal' },
          ].map(f => (
            <div key={f.key} style={{ marginBottom: 12 }}>
              <div style={styles.fieldLabel}>{f.label}</div>
              <input style={styles.input} value={form[f.key]} type="number"
                onChange={e => setForm(x => ({ ...x, [f.key]: e.target.value }))} />
            </div>
          ))}
        </div>

        <div style={styles.card}>
          <div style={styles.sectionLabel}>Wilayas assignées ({form.wilayas.length})</div>
          <div style={styles.wilayaGrid}>
            {WILAYAS.map(w => (
              <button key={w}
                style={{ ...styles.wilayaBtn, ...(form.wilayas.includes(w) ? styles.wilayaBtnActive : {}) }}
                onClick={() => toggleWilaya(w)}>
                {w}
              </button>
            ))}
          </div>
        </div>

        <button style={{ ...styles.btnSave, opacity: saving ? 0.6 : 1 }}
          onClick={onSubmit} disabled={saving}>
          {saving ? '⏳ En cours...' : submitLabel}
        </button>
      </div>
    </div>
  );

  if (vue === 'ajouter') return renderForm('Ajouter un commercial', creerCommercial, '✅ Créer le compte');
  if (vue === 'modifier') return renderForm(`Modifier — ${commEdite?.prenom}`, sauvegarderModif, '✅ Sauvegarder');

  // ── LISTE ──
  return (
    <div style={styles.app}>
      {confirmSuppr && (
        <div style={styles.overlay}>
          <div style={styles.confirmBox}>
            <div style={{ fontSize: 24, marginBottom: 8 }}>🗑</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#1C2833', marginBottom: 6 }}>Supprimer {confirmSuppr.prenom} {confirmSuppr.nom} ?</div>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 16 }}>Le compte Firestore sera supprimé. Les données associées restent.</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={styles.btnCancel} onClick={() => setConfirmSuppr(null)}>Annuler</button>
              <button style={{ ...styles.btnSuppr, opacity: saving ? 0.6 : 1 }}
                onClick={() => supprimerCommercial(confirmSuppr)} disabled={saving}>
                {saving ? '⏳' : '🗑 Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={styles.topbar}>
        <button onClick={onClose} style={styles.backBtn}>←</button>
        <div style={{ flex: 1 }}>
          <div style={styles.topbarTitle}>Gestion Commerciaux</div>
          <div style={styles.topbarSub}>{commerciaux.length} commercial(aux)</div>
        </div>
        <LangToggle />
        <button style={styles.addBtn} onClick={ouvrirAjouter}>➕ {t(lang, 'add')}</button>
      </div>

      <div style={styles.content}>
        {loading && <div style={styles.empty}>Chargement...</div>}
        {!loading && commerciaux.length === 0 && (
          <div style={styles.empty}>
            <div style={{ fontSize: 40 }}>👤</div>
            <div style={{ marginTop: 10, color: '#888' }}>Aucun commercial</div>
            <button style={{ ...styles.btnSave, marginTop: 16 }} onClick={ouvrirAjouter}>➕ Ajouter le premier</button>
          </div>
        )}
        {commerciaux.map((c, i) => (
          <div key={c.id} style={styles.commCard}>
            <div style={styles.commHeader}>
              <div style={{ ...styles.avatar, background: ['#FCEBEB','#E6F1FB','#EAF3DE'][i%3], color: ['#C0392B','#185FA5','#3B6D11'][i%3] }}>
                {(c.prenom || 'C')[0]}
              </div>
              <div style={{ flex: 1 }}>
                <div style={styles.commNom}>{c.prenom} {c.nom}</div>
                <div style={styles.commEmail}>{c.email}</div>
                <div style={styles.commSub}>
                  {(c.wilayas || []).slice(0, 3).join(' · ')}{c.wilayas?.length > 3 ? ` +${c.wilayas.length - 3}` : ''}
                </div>
              </div>
            </div>
            <div style={styles.commStats}>
              <div style={styles.statPill}>🎯 {c.objectifVisites || 60} visites/mois</div>
              <div style={styles.statPill}>💰 {(c.variableTotal || 0).toLocaleString()} DA</div>
            </div>
            <div style={styles.commActions}>
              <button style={styles.btnEdit} onClick={() => ouvrirModifier(c)}>✎ Modifier</button>
              <button style={styles.btnDel} onClick={() => setConfirmSuppr(c)}>🗑 Supprimer</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const styles = {
  app: { maxWidth: 480, margin: '0 auto', minHeight: '100vh', background: '#f5f5f5', display: 'flex', flexDirection: 'column', fontFamily: 'system-ui, sans-serif' },
  topbar: { background: '#1C2833', padding: '1rem 1.25rem', color: '#fff', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 },
  backBtn: { background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: 32, height: 32, color: '#fff', fontSize: 16, cursor: 'pointer', flexShrink: 0 },
  topbarTitle: { fontSize: 15, fontWeight: 600, flex: 1 },
  topbarSub: { fontSize: 11, opacity: 0.7, marginTop: 2 },
  addBtn: { background: '#3B6D11', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12, padding: '7px 12px', cursor: 'pointer', flexShrink: 0, fontWeight: 600 },
  content: { flex: 1, overflowY: 'auto', padding: '12px', display: 'flex', flexDirection: 'column', gap: 10 },
  card: { background: '#fff', borderRadius: 10, padding: '14px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column', gap: 0 },
  sectionLabel: { fontSize: 11, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 },
  fieldLabel: { fontSize: 12, color: '#555', marginBottom: 5 },
  input: { width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, fontFamily: 'system-ui', boxSizing: 'border-box' },
  erreur: { background: '#FCEBEB', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#A32D2D', border: '1px solid #f5c6c6' },
  succes: { background: '#EAF3DE', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#3B6D11', border: '1px solid #b6d99b' },
  wilayaGrid: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  wilayaBtn: { padding: '5px 10px', border: '1px solid #ddd', borderRadius: 20, background: '#f9f9f9', color: '#666', fontSize: 11, cursor: 'pointer' },
  wilayaBtnActive: { background: '#1C2833', border: '1px solid #1C2833', color: '#fff', fontWeight: 600 },
  btnSave: { padding: '13px', background: '#1C2833', border: 'none', borderRadius: 10, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  commCard: { background: '#fff', borderRadius: 10, padding: '14px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column', gap: 10 },
  commHeader: { display: 'flex', alignItems: 'center', gap: 12 },
  avatar: { width: 44, height: 44, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 700, flexShrink: 0 },
  commNom: { fontSize: 14, fontWeight: 700, color: '#1C2833' },
  commEmail: { fontSize: 11, color: '#185FA5', marginTop: 2 },
  commSub: { fontSize: 11, color: '#888', marginTop: 2 },
  commStats: { display: 'flex', gap: 8 },
  statPill: { background: '#f5f5f5', borderRadius: 20, padding: '4px 10px', fontSize: 11, color: '#555' },
  commActions: { display: 'flex', gap: 8 },
  btnEdit: { flex: 1, padding: '9px', border: '1px solid #ddd', borderRadius: 8, background: '#fff', color: '#1C2833', fontSize: 13, cursor: 'pointer', fontWeight: 500 },
  btnDel: { flex: 1, padding: '9px', border: '1px solid #f5c6c6', borderRadius: 8, background: '#FCEBEB', color: '#A32D2D', fontSize: 13, cursor: 'pointer', fontWeight: 500 },
  empty: { textAlign: 'center', padding: '3rem 1rem', color: '#888', display: 'flex', flexDirection: 'column', alignItems: 'center' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
  confirmBox: { background: '#fff', borderRadius: 14, padding: '24px', maxWidth: 320, width: '100%', textAlign: 'center', boxShadow: '0 8px 30px rgba(0,0,0,0.15)' },
  btnCancel: { flex: 1, padding: '10px', border: '1px solid #ddd', borderRadius: 10, background: '#fff', color: '#666', fontSize: 13, cursor: 'pointer' },
  btnSuppr: { flex: 1, padding: '10px', background: '#C0392B', border: 'none', borderRadius: 10, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
};
